import client from '../../config/api';
import {
  LOCAL_SET_CURRENT_USER,
  LOCAL_SET_USERS,
  LOCAL_SET_USERS_PENDING_APPROVAL,
  LOCAL_SET_USERS_LOGGED_IN_TODAY,
  LOCAL_SET_USERS_REJECTED,
  LOCAL_SET_USERS_WITHOUT_SEPIA_INFO,
} from './mutations/mutationTypes';

let usersProgressCount = 0;
let usersProgressTotal = 0;
let usersProgressCompleted = 0;
const usersProgressLabels = new Set();
const DEFAULT_MAX_USERS_PAGES = 200;
const DEFAULT_USERS_PAGE_LIMIT = 250;

function updateUsersProgressMessage(commit) {
  const labels = Array.from(usersProgressLabels);
  const suffix = labels.length ? ` (${labels.join(', ')})` : '';
  const progressValue =
    usersProgressTotal > 0
      ? Math.min(
          100,
          20 + Math.round((usersProgressCompleted / usersProgressTotal) * 80)
        )
      : 20;
  commit(
    'SET_PROGRESS_BAR',
    {
      key: 'users-loading',
      value: progressValue,
      message: `Loading users${suffix}...`,
    },
    { root: true }
  );
}

function startUsersProgress(commit, label) {
  if (usersProgressCount === 0) {
    usersProgressTotal = 0;
    usersProgressCompleted = 0;
  }
  usersProgressCount += 1;
  usersProgressTotal += 1;
  if (label) {
    usersProgressLabels.add(label);
  }
  updateUsersProgressMessage(commit);
}

function endUsersProgress(commit, label) {
  usersProgressCount = Math.max(0, usersProgressCount - 1);
  usersProgressCompleted = Math.min(
    usersProgressTotal,
    usersProgressCompleted + 1
  );
  if (label) {
    usersProgressLabels.delete(label);
  }
  if (usersProgressCount === 0) {
    commit(
      'SET_PROGRESS_BAR',
      { key: 'users-loading', value: null, message: '' },
      { root: true }
    );
    usersProgressTotal = 0;
    usersProgressCompleted = 0;
    usersProgressLabels.clear();
  } else {
    updateUsersProgressMessage(commit);
  }
}

function handleError(error) {
  throw error;
}

function getUsersListFromPayload(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.users)) {
    return payload.users;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
}

function isGatewayFailure(error) {
  const status = error?.response?.status;
  return status === 502 || status === 503 || status === 504;
}

function stableSerializeCursor(value) {
  if (value === null || value === undefined) {
    return String(value);
  }

  if (typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerializeCursor(item)).join(',')}]`;
  }

  const keys = Object.keys(value).sort();
  const serializedPairs = keys.map(
    (key) => `${JSON.stringify(key)}:${stableSerializeCursor(value[key])}`
  );
  return `{${serializedPairs.join(',')}}`;
}

function parseUsersPagePayload(payload) {
  const items = getUsersListFromPayload(payload);
  const lastEvaluatedKey =
    payload?.lastEvaluatedKey || payload?.LastEvaluatedKey || null;
  const countValue = Number(payload?.count ?? payload?.Count);

  return {
    items,
    count: Number.isFinite(countValue) ? countValue : items.length,
    lastEvaluatedKey,
    isPaged: !Array.isArray(payload),
  };
}

async function fetchUsersCollection(
  query = {},
  { loadAllPages = true, maxPages = DEFAULT_MAX_USERS_PAGES } = {}
) {
  const aggregatedItems = [];
  const initialStartKey = query?.startKey || null;
  const normalizedMaxPages =
    Number.isInteger(maxPages) && maxPages > 0
      ? maxPages
      : DEFAULT_MAX_USERS_PAGES;
  const visitedCursorTokens = new Set();

  const fetchPage = async (startKey, pageNumber) => {
    if (pageNumber >= normalizedMaxPages) {
      return {
        items: aggregatedItems,
        count: aggregatedItems.length,
        lastEvaluatedKey: startKey || null,
      };
    }

    const params = {
      ...query,
      ...(startKey ? { startKey } : {}),
    };

    const { data } = await client.get('/users', {
      params,
    });

    const parsedPage = parseUsersPagePayload(data);
    aggregatedItems.push(...parsedPage.items);

    if (!parsedPage.isPaged || !loadAllPages) {
      return {
        items: aggregatedItems,
        count: parsedPage.count,
        lastEvaluatedKey: parsedPage.lastEvaluatedKey,
      };
    }

    const nextStartKey = parsedPage.lastEvaluatedKey;
    const hasNextPage = Boolean(nextStartKey);
    const nextCursorToken = stableSerializeCursor(nextStartKey);

    if (!hasNextPage || visitedCursorTokens.has(nextCursorToken)) {
      return {
        items: aggregatedItems,
        count: aggregatedItems.length,
        lastEvaluatedKey: nextStartKey || null,
      };
    }

    visitedCursorTokens.add(nextCursorToken);

    return fetchPage(nextStartKey, pageNumber + 1);
  };

  return fetchPage(initialStartKey, 0);
}

export default {
  async ADD_FAVORITE(_, itemId) {
    const { data } = await client.post(`users/current/favorites/${itemId}`);
    return data;
  },

  async USERS_GET_CURRENT({ commit }) {
    try {
      const result = await client.get('/users/current');
      const user = result?.data;
      if (
        !user ||
        typeof user !== 'object' ||
        Array.isArray(user) ||
        !user._id
      ) {
        const invalidCurrentUserError = new Error('Current user not found');
        invalidCurrentUserError.status = 404;
        throw invalidCurrentUserError;
      }
      commit(LOCAL_SET_CURRENT_USER, user);
      commit('SET_CURRENT_USER', user, { root: true }); // We still have the current user not refactored on root state
      return user;
    } catch (error) {
      console.warn(error);
      handleError(error);
    }
  },

  /**
   * Get the list of users to show on admin's page
   */
  async GET_USERS({ commit }, query) {
    startUsersProgress(commit, 'Approved');
    try {
      const normalizedQuery = {
        ...(query || {}),
        limit: query?.limit || DEFAULT_USERS_PAGE_LIMIT,
      };
      const usersResult = await fetchUsersCollection(normalizedQuery);
      commit(LOCAL_SET_USERS, usersResult.items);
      return usersResult.items;
    } catch (error) {
      if (!isGatewayFailure(error)) {
        throw error;
      }

      try {
        // Fallback for large production datasets when full /users scan times out.
        const { data } = await client.get('/users', {
          params: {
            ...(query || {}),
            directory: true,
          },
        });

        const users = getUsersListFromPayload(data);
        commit(LOCAL_SET_USERS, users);
        return users;
      } catch (fallbackError) {
        console.warn(
          '[UsersModule/GET_USERS] Both primary and fallback users requests failed.',
          {
            primaryStatus: error?.response?.status,
            fallbackStatus: fallbackError?.response?.status,
          }
        );
        commit(LOCAL_SET_USERS, []);
        return [];
      }
    } finally {
      endUsersProgress(commit, 'Approved');
    }
  },

  /**
   * Get the list of users pending approval
   */
  async GET_USERS_PENDING_APPROVAL({ commit }, query) {
    startUsersProgress(commit, 'Pending Approval');
    const queryWithPendingApproval = { pendingApproval: true, ...query };
    try {
      const result = await client.get('/users', {
        params: {
          ...queryWithPendingApproval,
        },
      });
      commit(LOCAL_SET_USERS_PENDING_APPROVAL, result.data);
    } finally {
      endUsersProgress(commit, 'Pending Approval');
    }
  },

  async GET_USERS_LOGGED_IN_TODAY({ commit }, query) {
    // eslint-disable-next-line
    startUsersProgress(commit, "Today's Logins");
    const now = new Date();
    const lastMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    const queryWithLoggedInToday = {
      loggedInAfter: lastMidnight.getTime(),
      ...query,
    };
    try {
      const result = await client.get('/users', {
        params: {
          ...queryWithLoggedInToday,
        },
      });
      commit(LOCAL_SET_USERS_LOGGED_IN_TODAY, result.data);
    } finally {
      // eslint-disable-next-line
      endUsersProgress(commit, "Today's Logins");
    }
  },

  async GET_USERS_REJECTED({ commit }, query) {
    startUsersProgress(commit, 'Rejected');
    const queryWithRejected = { rejected: true, ...query };
    try {
      const result = await client.get('/users', {
        params: {
          ...queryWithRejected,
        },
      });
      commit(LOCAL_SET_USERS_REJECTED, result.data);
    } finally {
      endUsersProgress(commit, 'Rejected');
    }
  },

  async GET_USERS_WITHOUT_SEPIA_INFO({ commit }, query) {
    startUsersProgress(commit, 'Inactive');
    const queryWithNoSepiaInfo = { noSepiaInfo: true, ...query };
    try {
      const result = await client.get('/users', {
        params: {
          ...queryWithNoSepiaInfo,
        },
      });
      commit(LOCAL_SET_USERS_WITHOUT_SEPIA_INFO, result.data);
    } finally {
      endUsersProgress(commit, 'Inactive');
    }
  },
};
